import{characterDNAForFighter,characterDNASkillBias,type CharacterDNA}from'./characterDNA'
import{organFunction,type LivingState}from'./lifeOmega'
import type{AttackKind,Combatant}from'./simulation'

export type CharacterMentalSnapshot={
 focus:number;confidence:number;fear:number;curiosity:number;mentalLoad:number;commitment:number
}
export type CharacterPhenotype={
 fighterId:string
 dnaFingerprint:string
 physicalIntegrity:number
 neuralIntegrity:number
 consciousness:number
 painLoad:number
 fatigueLoad:number
 sensoryExpression:{
  vision:number;hearing:number;olfaction:number;gustation:number;proprioception:number;vestibular:number;tactile:number;
  thermal:number;chemical:number;electromagnetic:number;echolocation:number;vibration:number;spatial:number;energetic:number
 }
 motorExpression:{
  speed:number;balance:number;precision:number;weight:number;elasticity:number;recovery:number
 }
 cognitiveExpression:{
  focus:number;confidence:number;threatInhibition:number;commitment:number;curiosity:number
  cognitiveStability:number;executiveCapacity:number;volitionalCapacity:number
  attention:number;episodicRecall:number;semanticAccess:number;imagination:number;causalReasoning:number
  spatialReasoning:number;temporalReasoning:number;theoryOfMind:number;language:number;sensorFusion:number
 }
 powerExpression:{
  amplitude:number;control:number;precision:number;recovery:number;environmentCoupling:number
 }
 skillReadiness:Record<AttackKind,number>
 flightReadiness:number
 quantumReadiness:number
 transformationReadiness:number
}

const clamp=(v:number,lo=0,hi=1)=>Math.max(lo,Math.min(hi,Number.isFinite(v)?v:lo))
const geometric=(...values:number[])=>values.length?Math.pow(values.reduce((product,value)=>product*Math.max(.0001,clamp(value)),1),1/values.length):0
const normalizedHealth=(state:Combatant)=>state.maxHealth>0?clamp(state.health/state.maxHealth):0

function mentalOrBaseline(dna:CharacterDNA,mind?:Partial<CharacterMentalSnapshot>):CharacterMentalSnapshot{
 return{
  focus:clamp(mind?.focus??(.42+dna.mind.discipline*.3+dna.mind.precision*.28)),
  confidence:clamp(mind?.confidence??(.35+dna.mind.courage*.34+dna.physiology.structuralStability*.2)),
  fear:clamp(mind?.fear??(.28+dna.mind.caution*.24-dna.mind.courage*.18)),
  curiosity:clamp(mind?.curiosity??dna.mind.drives.curiosity),
  mentalLoad:clamp(mind?.mentalLoad??.15),
  commitment:clamp(mind?.commitment??(.35+dna.mind.aggression*.25+dna.mind.courage*.2)),
 }
}

export function deriveCharacterPhenotype(
 fighterId:string,
 life:LivingState,
 state:Combatant,
 mind?:Partial<CharacterMentalSnapshot>,
):CharacterPhenotype{
 const dna=characterDNAForFighter(fighterId),mental=mentalOrBaseline(dna,mind)
 const health=normalizedHealth(state),energyOrgan=clamp(organFunction(life,'energy')),movementOrgan=clamp(organFunction(life,'locomotion'))
 const physicalIntegrity=geometric(health,life.circulation,life.oxygenation,movementOrgan,dna.physiology.durability)
 const neuralIntegrity=geometric(life.neuralIntegrity,life.consciousness,dna.physiology.neuralSpeed)
 const consciousness=clamp(life.consciousness)
 const painLoad=clamp(life.pain*dna.senses.painSensitivity)
 const fatigueLoad=clamp(life.fatigue*(1-dna.physiology.energyEfficiency*.35))
 const architecture=dna.mind.architecture
 const vision=geometric(life.vision,dna.senses.vision,neuralIntegrity)
 const hearing=geometric(life.hearing,dna.senses.hearing,neuralIntegrity)
 const olfaction=geometric(dna.senses.olfaction,life.oxygenation,neuralIntegrity)
 const gustation=geometric(dna.senses.gustation,consciousness,neuralIntegrity)
 const proprioception=geometric(life.proprioception,dna.senses.proprioception,neuralIntegrity)
 const vestibular=geometric(life.balance,dna.senses.vestibular,neuralIntegrity)
 const tactile=geometric(1-painLoad*.28,dna.senses.tactile,neuralIntegrity)
 const thermal=geometric(dna.senses.thermalSense,life.circulation,neuralIntegrity)
 const chemical=geometric(dna.senses.chemicalSense,olfaction,neuralIntegrity)
 const energetic=geometric(energyOrgan,dna.senses.energeticSense,1-fatigueLoad)
 const electromagnetic=geometric(dna.senses.electromagneticSense,energetic,neuralIntegrity)
 const vibration=geometric(dna.senses.vibrationSense,tactile,proprioception)
 const echolocation=geometric(dna.senses.echolocation,hearing,neuralIntegrity)
 const baseSpatial=clamp(vision*.24+hearing*.09+proprioception*.2+vestibular*.22+echolocation*.1+vibration*.08+electromagnetic*.07)
 const sensorFusion=geometric(architecture.sensorFusion,baseSpatial,neuralIntegrity,consciousness)
 const spatial=clamp(baseSpatial*(.58+dna.senses.spatialSense*.24+sensorFusion*.18))
 const attention=geometric(neuralIntegrity,consciousness,architecture.attentionControl,1-mental.mentalLoad*.45)
 const episodicRecall=geometric(neuralIntegrity,attention,architecture.episodicMemory,dna.learning.memoryDepth)
 const semanticAccess=geometric(neuralIntegrity,attention,architecture.semanticMemory,dna.mind.learning)
 const imagination=geometric(neuralIntegrity,consciousness,architecture.imagination,dna.mind.creativity)
 const causalReasoning=geometric(neuralIntegrity,attention,architecture.causalReasoning,architecture.planning)
 const spatialReasoning=geometric(neuralIntegrity,attention,architecture.spatialReasoning,spatial)
 const temporalReasoning=geometric(neuralIntegrity,attention,architecture.temporalReasoning,dna.mind.prediction)
 const theoryOfMind=geometric(neuralIntegrity,attention,architecture.theoryOfMind,architecture.socialCognition)
 const language=geometric(neuralIntegrity,attention,architecture.languageCapacity,semanticAccess)
 const cognitiveStability=geometric(neuralIntegrity,architecture.selfModel,architecture.emotionalIntegration,architecture.metacognition,attention)
 const executiveCapacity=geometric(neuralIntegrity,architecture.workingMemory,architecture.planning,architecture.impulseControl,attention,causalReasoning)
 const volitionalCapacity=geometric(consciousness,architecture.volition,architecture.goalPersistence,architecture.selfModel,1-mental.mentalLoad*.35)
 const threatInhibition=clamp(
  (mental.fear*.4+mental.mentalLoad*.26+painLoad*.16+(1-neuralIntegrity)*.2)*
  (1-architecture.impulseControl*.18-architecture.metacognition*.12)+
  (1-cognitiveStability)*.16
 )
 const focus=clamp(
  mental.focus*(1-mental.mentalLoad*.32)*(1-painLoad*.2)*
  (.5+neuralIntegrity*.2+architecture.workingMemory*.16+architecture.selfModel*.14)
 )
 const confidence=clamp(
  mental.confidence*(.42+physicalIntegrity*.18+neuralIntegrity*.14+architecture.selfModel*.14+architecture.emotionalIntegration*.12)*
  (1-mental.fear*.14)
 )
 const commitment=clamp(
  mental.commitment*(.38+confidence*.18+focus*.14+volitionalCapacity*.2+architecture.goalPersistence*.1)*
  (1-threatInhibition*.16)
 )
 const motorSpeed=geometric(physicalIntegrity,neuralIntegrity,1-fatigueLoad,dna.physiology.neuralSpeed)
 const motorBalance=geometric(life.balance,dna.physiology.balance,dna.senses.vestibular,neuralIntegrity)
 const motorPrecision=geometric(focus,dna.mind.precision,dna.power.precision,proprioception)
 const recovery=geometric(1-fatigueLoad,dna.physiology.energyEfficiency,dna.power.recovery,energyOrgan)
 const powerControl=geometric(dna.power.control,focus,neuralIntegrity,energetic)
 const powerAmplitude=clamp(dna.power.amplitude*(.45+energyOrgan*.35+commitment*.2)*(1-fatigueLoad*.24))
 const powerPrecision=geometric(dna.power.precision,motorPrecision,powerControl)
 const stamina=clamp(state.stamina/100),superMeter=clamp(state.superMeter/100)
 const readiness={} as Record<AttackKind,number>
 for(const kind of ['light','heavy','special','throw'] as const){
  const base=characterDNASkillBias(fighterId,kind),resource=kind==='special'?superMeter:stamina
  const force=kind==='heavy'||kind==='throw'?physicalIntegrity:motorSpeed
  const control=kind==='special'?powerControl:motorPrecision
  readiness[kind]=clamp(base*(.24+resource*.18+force*.17+control*.17+commitment*.09+executiveCapacity*.08+volitionalCapacity*.07)*(1-painLoad*.18)*(1-fatigueLoad*.2))
 }
 if(state.downed||state.hitstun>0||consciousness<.22)for(const kind of ['light','heavy','special','throw'] as const)readiness[kind]*=.12
 if(superMeter<1)readiness.special*=.35
 return{
  fighterId,dnaFingerprint:dna.fingerprint,physicalIntegrity,neuralIntegrity,consciousness,painLoad,fatigueLoad,
  sensoryExpression:{vision,hearing,olfaction,gustation,proprioception,vestibular,tactile,thermal,chemical,electromagnetic,echolocation,vibration,spatial,energetic},
  motorExpression:{speed:motorSpeed,balance:motorBalance,precision:motorPrecision,weight:dna.expression.motionWeight,elasticity:dna.physiology.elasticity,recovery},
  cognitiveExpression:{
   focus,confidence,threatInhibition,commitment,curiosity:mental.curiosity,cognitiveStability,executiveCapacity,volitionalCapacity,
   attention,episodicRecall,semanticAccess,imagination,causalReasoning,spatialReasoning,temporalReasoning,theoryOfMind,language,sensorFusion,
  },
  powerExpression:{amplitude:powerAmplitude,control:powerControl,precision:powerPrecision,recovery,environmentCoupling:dna.power.environmentCoupling},
  skillReadiness:readiness,
  flightReadiness:dna.capabilities.flight?clamp(geometric(vestibular,spatial,motorBalance,energyOrgan,consciousness)*(.6+dna.combat.aerialMastery*.4)):0,
  quantumReadiness:dna.capabilities.quantum?clamp(geometric(powerControl,spatial,focus,stamina)*(state.quantumCooldown??0)<=0?1:.1):0,
  transformationReadiness:dna.capabilities.transformation?clamp(geometric(powerAmplitude,powerControl,energyOrgan,consciousness)*(1-fatigueLoad*.25)):0,
 }
}
